import {
  PeeqdFeatureBlock,
  PeeqdHowItWorksContent,
  PeeqdFaqItem,
  PeeqdToolId,
  PeeqdHomeWhoItem,
  PEEQD_HOME_FEATURES,
  PEEQD_HOME_WHO,
  PEEQD_HOME_FACTS,
  PEEQD_HOME_HOW_IT_WORKS,
  PEEQD_TOOL_FEATURES,
  PEEQD_TOOL_HOW_IT_WORKS,
  PEEQD_FAQ,
  PEEQD_TOOL_FAQ,
} from "@/config/peeqdContent";
import { CheckIcon, CompetitorsIcon } from "./icons";

export default function PeeqdProofStrip() {
  const items = [
    { title: "Hourly", sub: "snapshots & diffs" },
    { title: "Real", sub: "timestamps, not guesses" },
    { title: "Events only", sub: "no photos ever stored" },
    { title: "1 click", sub: "cancel, billed monthly" },
  ];

  return (
    <section className="mx-auto max-w-6xl py-6">
      <div className="grid grid-cols-2 gap-5 rounded-3xl border border-line bg-surface px-5 py-5 text-center shadow-soft sm:px-8 md:grid-cols-4">
        {items.map((item) => (
          <div key={item.title}>
            <p className="font-display text-[22px] font-extrabold">{item.title}</p>
            <p className="mt-0.5 text-[12.5px] text-muted">{item.sub}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ChangelogVisual() {
  const events = [
    { type: "follower-new", user: "@v3li3e", time: "Mon 2:14pm" },
    { type: "follower-new", user: "@olesya.ww", time: "Mon 9:02am" },
    { type: "follower-lost", user: "@_kh_andy_", time: "Sun 6:41pm" },
    { type: "following-new", user: "@travelwithnora", time: "Sat 11:20am" },
  ] as const;

  const style = {
    "follower-new": { bg: "bg-mint-soft", text: "text-mint", prefix: "+ " },
    "follower-lost": { bg: "bg-rose-soft", text: "text-rose-deep", prefix: "− " },
    "following-new": { bg: "bg-mint-soft", text: "text-mint", prefix: "→ " },
    "following-lost": { bg: "bg-rose-soft", text: "text-rose-deep", prefix: "← " },
  };

  return (
    <div className="rounded-3xl border border-line bg-surface p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[13.5px] font-semibold">Follow changes · @otherside.studio</p>
        <span className="font-mono text-[11px] text-muted">this week</span>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2 text-[10px] font-bold uppercase tracking-wider text-muted">
        <span>Followers</span>
        <span className="text-right">Following</span>
      </div>
      <div className="space-y-2 font-mono text-[12.5px]">
        {events.map((event) => {
          const s = style[event.type];
          return (
            <div
              key={`${event.type}-${event.user}`}
              className={`flex items-center justify-between rounded-lg px-3 py-2 ${s.bg}`}
            >
              <span className={s.text}>
                {s.prefix}
                {event.user}
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-center text-[11px] text-muted">
        +12 followers · −2 lost · +5 followed · −1 unfollowed
      </p>
    </div>
  );
}

function LikesAnalyticsVisual() {
  const rows = [
    { place: 1, user: "carolebamford", name: "Carole Bamford", likes: 11, trend: "same" as const },
    { place: 2, user: "iandore", name: "Ian Dore", likes: 6, trend: "up" as const, trendBy: 5 },
    { place: 3, user: "coco.suze", name: "Coco Suze", likes: 5, trend: "up" as const, trendBy: 2 },
  ];

  const postStats = [
    { label: "Avg likes / post", value: "71" },
    { label: "Engagement rate", value: "3.4%" },
    { label: "Posts analyzed", value: "12" },
  ];

  return (
    <div className="rounded-3xl border border-line bg-surface p-5 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[13.5px] font-semibold">Who liked their posts</p>
        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-mono text-[11px] font-bold text-ink">
          109
        </span>
      </div>

      <div className="mb-3 grid grid-cols-3 gap-2">
        {postStats.map((stat) => (
          <div key={stat.label} className="rounded-xl bg-rose-softer px-2.5 py-2 text-center">
            <p className="font-mono text-[15px] font-bold text-ink">{stat.value}</p>
            <p className="text-[10px] text-muted">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="divide-y divide-line rounded-xl border border-line">
        {rows.map((row) => (
          <div
            key={row.user}
            className="flex items-center gap-2.5 px-3 py-2.5"
          >
            <span className="w-4 text-center font-mono text-[12px] font-semibold text-ink">
              {row.place}
            </span>
            <div className="h-8 w-8 shrink-0 rounded-full bg-line" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-ink">@{row.user}</p>
              <p className="truncate text-[11px] text-muted">{row.name}</p>
            </div>
            <span className="flex shrink-0 items-center gap-1 text-[12px] font-semibold text-ink">
              <svg className="h-3 w-3 text-amber-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {row.likes}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityReportVisual() {
  return (
    <div className="rounded-3xl border border-line bg-surface p-5 shadow-card">
      <div className="mb-4 flex items-start justify-between gap-2">
        <p className="text-[12px] font-bold uppercase tracking-wider text-muted">Latest Activity</p>
        <span className="text-[11.5px] font-medium text-muted">Jul 1 – Jul 7</span>
      </div>

      <div className="space-y-3 text-[13px] leading-relaxed text-ink">
        <div className="flex gap-3">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-rose-softer text-rose-deep">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </span>
          <p>
            <span className="font-semibold">@otherside.studio</span> is{" "}
            <span className="font-bold text-emerald-600">moderately active</span>. Their latest 12
            posts received <span className="font-bold">847</span> likes from{" "}
            <span className="font-bold">109</span> accounts - about{" "}
            <span className="font-bold">71</span> likes per post.
          </p>
        </div>

        <div className="flex gap-3">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-rose-softer text-rose-deep">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </span>
          <p>
            Top liker this week: <span className="font-semibold">@carolebamford</span> with{" "}
            <span className="font-bold">11</span> likes across their posts.
          </p>
        </div>

        <div className="flex gap-3">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-rose-softer text-rose-deep">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
          </span>
          <p>
            Follower/following changes: <span className="font-bold">+12</span> followers,{" "}
            <span className="font-bold">−2</span> lost; followed{" "}
            <span className="font-bold">5</span> accounts, unfollowed{" "}
            <span className="font-bold">1</span>.
          </p>
        </div>
      </div>
    </div>
  );
}

function CompetitorStatsVisual() {
  return (
    <div className="rounded-3xl border border-line bg-surface p-5 shadow-card">
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "follows / wk", value: "+61", color: "text-mint" },
          { label: "unfoll / wk", value: "−18", color: "text-rose-deep" },
          { label: "stories / wk", value: "27", color: "" },
          { label: "accounts", value: "4 / 40", color: "" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl bg-rose-softer p-3.5">
            <p className="font-mono text-[11px] text-muted">{s.label}</p>
            <p className={`font-mono text-[20px] font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditChartVisual() {
  return (
    <div className="rounded-3xl border border-line bg-surface p-5 shadow-card">
      <div className="flex items-center gap-5">
        <svg viewBox="0 0 42 42" className="h-28 w-28 -rotate-90">
          <circle cx="21" cy="21" r="15.9" fill="none" stroke="#FCE4EA" strokeWidth="6" />
          <circle cx="21" cy="21" r="15.9" fill="none" stroke="#EF5F7E" strokeWidth="6" strokeDasharray="78 100" strokeLinecap="round" />
        </svg>
        <div>
          <p className="font-mono text-[26px] font-bold leading-none">78<span className="text-[16px]">%</span></p>
          <p className="mt-1 text-[12.5px] text-muted">real, active<br />followers</p>
        </div>
      </div>
      <div className="mt-4 space-y-2.5">
        <div>
          <div className="mb-1 flex justify-between text-[12px]"><span className="text-muted">Suspicious / bots</span><span className="font-mono">22%</span></div>
          <div className="h-1.5 rounded-full bg-rose-soft"><div className="grad-bg h-full rounded-full" style={{ width: "22%" }} /></div>
        </div>
        <div>
          <div className="mb-1 flex justify-between text-[12px]"><span className="text-muted">Engagement rate</span><span className="font-mono">3.4%</span></div>
          <div className="h-1.5 rounded-full bg-rose-soft"><div className="h-full rounded-full bg-mint" style={{ width: "54%" }} /></div>
        </div>
      </div>
    </div>
  );
}

function FeatureVisual({ visual }: { visual: PeeqdFeatureBlock["visual"] }) {
  switch (visual) {
    case "changelog":
      return <ChangelogVisual />;
    case "likes-analytics":
      return <LikesAnalyticsVisual />;
    case "activity-report":
      return <ActivityReportVisual />;
    case "story-alert":
      return <StoryAlertVisual />;
    case "competitor-stats":
      return <CompetitorStatsVisual />;
    case "audit-chart":
      return <AuditChartVisual />;
  }
}

function StoryAlertVisual() {
  return (
    <div className="rounded-3xl border border-line bg-surface p-5 shadow-card">
      <div className="mb-3 flex items-center gap-3 rounded-2xl bg-ink p-4 text-white">
        <span className="grid h-9 w-9 place-items-center rounded-xl grad-bg">
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /></svg>
        </span>
        <div className="leading-tight">
          <p className="text-[13.5px] font-semibold">Story from @otherside.studio</p>
          <p className="text-[11.5px] text-white/60">now · view before it expires</p>
        </div>
        <span className="ml-auto font-mono text-[11px] text-white/60">now</span>
      </div>
      <div className="flex items-center gap-3 text-[12.5px] text-muted">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-mint" />Anonymous</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet" />Push in &lt; 60s</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose" />Nothing stored</span>
      </div>
    </div>
  );
}

function FeatureBlock({ block }: { block: PeeqdFeatureBlock }) {
  return (
    <div className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14 ${block.reversed ? "" : ""}`}>
      <div className={block.reversed ? "order-1 lg:order-2" : ""}>
        <span className="text-[12px] font-semibold uppercase tracking-widest text-rose-deep">{block.label}</span>
        <h2 className="mt-2 font-display text-[28px] font-extrabold leading-tight tracking-tight sm:text-[34px]">
          {block.title}
        </h2>
        <p className="mt-3 max-w-lg text-[16px] leading-relaxed text-muted">{block.description}</p>
        {block.bullets && (
          <ul className="mt-5 space-y-2.5 text-[14.5px]">
            {block.bullets.map((t) => (
              <li key={t} className="flex gap-2.5">
                <CheckIcon className="h-5 w-5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={block.reversed ? "order-2 lg:order-1" : ""}>
        <FeatureVisual visual={block.visual} />
      </div>
    </div>
  );
}

export function PeeqdHomeFeatures() {
  return (
    <section id="features" className="mx-auto max-w-5xl scroll-mt-8 px-6 pb-20 pt-12 sm:pt-16">
      <h2 className="text-center font-display text-4xl font-extrabold tracking-tight md:text-5xl">
        Everything worth knowing. <span className="grad-text">One report.</span>
      </h2>
      <div className="mt-12 space-y-16 sm:space-y-24">
        {PEEQD_HOME_FEATURES.map((block) => (
          <FeatureBlock key={block.label} block={block} />
        ))}
      </div>
    </section>
  );
}

function WhoUseCaseIcon({ id }: { id: PeeqdHomeWhoItem["id"] }) {
  const cls = "h-[18px] w-[18px]";

  switch (id) {
    case "watch":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
        </svg>
      );
    case "question":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </svg>
      );
    case "growth":
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
      );
    case "competitors":
      return <CompetitorsIcon className={cls} />;
  }
}

export function PeeqdHomeWho() {
  return (
    <section id="who" className="mx-auto max-w-5xl scroll-mt-8 px-6 pb-20">
      <h2
        className="text-center font-display text-4xl font-extrabold tracking-tight md:text-5xl"
        dangerouslySetInnerHTML={{ __html: PEEQD_HOME_WHO.titleHtml }}
      />
      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {PEEQD_HOME_WHO.items.map((item) => (
          <div key={item.id} className="rounded-xl2 bg-white p-7 ring-1 ring-black/5">
            <span className="mb-4 grid h-10 w-10 place-items-center rounded-xl bg-rose-softer text-rose-deep">
              <WhoUseCaseIcon id={item.id} />
            </span>
            <p className="mb-2 font-display text-lg font-bold">{item.title}</p>
            <p className="text-sm leading-relaxed text-muted">{item.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PeeqdToolFeatures({ toolId }: { toolId: PeeqdToolId }) {
  const block = PEEQD_TOOL_FEATURES[toolId];
  return (
    <section className="mx-auto max-w-6xl py-12 sm:py-16">
      <FeatureBlock block={block} />
    </section>
  );
}

export function PeeqdHomeFacts() {
  const { titleHtml, intro, steps, footnotes } = PEEQD_HOME_FACTS;

  return (
    <section id="facts" className="mx-auto max-w-4xl scroll-mt-8 px-6 pb-20">
      <h2
        className="text-center font-display text-4xl font-extrabold tracking-tight md:text-5xl"
        dangerouslySetInnerHTML={{ __html: titleHtml }}
      />

      <div className="mt-12 rounded-3xl border border-line bg-surface p-8 shadow-card md:p-10">
        <p className="text-sm leading-relaxed text-muted md:text-[15px]">{intro}</p>

        <ol className="mt-8 space-y-7">
          {steps.map((step, index) => (
            <li key={step.title} className="flex gap-4">
              <span className="shrink-0 font-display text-2xl font-black grad-text">
                {index + 1}
              </span>
              <div>
                <p className="text-sm font-semibold text-ink md:text-base">{step.title}</p>
                <p
                  className="mt-1 text-sm leading-relaxed text-muted md:text-[15px]"
                  dangerouslySetInnerHTML={{ __html: step.bodyHtml }}
                />
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-9 space-y-3 border-t border-line pt-7 text-sm leading-relaxed text-muted">
          {footnotes.map((note) => (
            <p key={note.label}>
              <span className="font-medium text-ink">{note.label}</span> {note.body}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PeeqdHowItWorks({ content }: { content?: PeeqdHowItWorksContent }) {
  const data = content ?? PEEQD_HOME_HOW_IT_WORKS;

  return (
    <section id="how-it-works" className="mx-auto max-w-6xl scroll-mt-8 py-10">
      <div className="mx-auto max-w-2xl text-center">
        <span className="text-[12px] font-semibold uppercase tracking-widest text-rose-deep">How it works</span>
        <h2
          className="mt-2 font-display text-[28px] font-extrabold tracking-tight sm:text-[36px]"
          dangerouslySetInnerHTML={{ __html: data.titleHtml }}
        />
        <p className="mt-3 text-[15.5px] text-muted">{data.description}</p>
      </div>
      <div className="mt-9 grid gap-4 md:grid-cols-3">
        {data.steps.map((s) => (
          <div key={s.n} className="rounded-3xl border border-line bg-surface p-6 shadow-soft">
            <span className="font-mono text-[13px] text-rose-deep">{s.n}</span>
            <h3 className="mt-2 font-display text-[19px] font-bold">{s.title}</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-muted">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function getToolHowItWorks(toolId: PeeqdToolId) {
  return PEEQD_TOOL_HOW_IT_WORKS[toolId];
}

export function getToolFaq(toolId: PeeqdToolId): PeeqdFaqItem[] {
  return PEEQD_TOOL_FAQ[toolId] ?? PEEQD_FAQ;
}
